import * as classNames from 'classnames'
import { Field, Formik, FormikProps, FieldProps } from 'formik'
import { ServicePromoFareIntervalOptions, ServicePromoFareIntervals } from '../../../constants/service-promo-fare'
import * as React from 'react'
import Select from 'react-select'
import * as Yup from 'yup'

import ImageUpload from '../../ui/ImageUpload'
import Toggle from '../../ui/Toggle'
import {
  IServiceCreateObject,
  IServiceObject,
  IServiceOptions,
  IServiceUpdateObject,
  IStoreState,
  NotificationOptions
} from '../../../types/index'
import { IServiceProgramObject } from '../../../types/service-program'
import PhotoUploadIcon from '../../icons/ui/PhotoUploadIcon'
import { service } from 'src/state/reducers/services'
import { organizations } from 'src/state/selectors'
import InnerRadioButton from 'src/components/riders/createrider/personalinfo/InnerRadioButton'
import UIMessages from 'src/utils/ui-messages'
import { NotificationOptionsDisplay, ServiceOptionDisplay, viewOptionTypes } from 'src/constants/service'
import { errors } from 'src/state/reducers/errors'

const selectInput = ({ field, form }: FieldProps<any>) => {
  const handleChange = (selected: any) => {
    form.setFieldValue(field.name, selected.value)
  }
  const selectedOption = ServicePromoFareIntervalOptions.find(m => m.value === field.value)
  return <Select value={selectedOption} options={ServicePromoFareIntervalOptions} onChange={handleChange} />
}

interface IServiceEditMainInfoProps {
  userId: string
  serviceId?: string
  orgId?: string
  service?: IServiceObject
  serviceProgram?: IServiceProgramObject
  servicePrograms: IServiceProgramObject[]
  onComponentSubmit: (service: IServiceCreateObject | IServiceUpdateObject) => Promise<IServiceObject>
  onStepSuccessfullyCompleted?: () => void
  onComponentMount: () => void
  formRef: any
}

const viewOptions: ISelectOption[] = [
  {
    label: UIMessages.service.publicService,
    value: viewOptionTypes.public
  },
  {
    label: UIMessages.service.privateService,
    value: viewOptionTypes.private
  }
]

class ServiceEditMainInfo extends React.Component<IServiceEditMainInfoProps, IStoreState> {
  private validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    serviceProgram: Yup.mixed().required('Service program is required'),
    requestTimeoutDuration: Yup.number()
      .min(1, 'Value should not be less than 1')
      .max(240, 'Value should not exceed 240'),
    maxTravelDistance: Yup.number()
      .min(1, 'Value should not be less than 1')
      .max(20000, 'Value should not exceed 20000'),
    rideReconfirmation: Yup.number()
      .required('Value is required')
      .min(1, 'Value should not be less than 1')
  })

  constructor(props: IServiceEditMainInfoProps) {
    super(props)
  }

  public componentDidMount() {
    this.props.onComponentMount()
  }

  public render() {
    const { service, serviceProgram, servicePrograms } = this.props
    const viewOption = service && service.viewOption ? service.viewOption : viewOptions[1].value
    const viewOptionInitVal = viewOptions.find(item => item.value === viewOption)
    return (
      <Formik
        ref={this.props.formRef}
        enableReinitialize={true}
        initialValues={{
          name: service ? service.name : '',
          description: service ? service.description : '',
          phoneNumber: service ? service.phoneNumber : '',
          serviceProgram: serviceProgram ? serviceProgramToOption(serviceProgram) : null,
          isMembershipRequired: service ? service.isMembershipRequired : true,
          internalName: service ? service.internalName : '',
          limitEnabled: !!(service && service.tripLimit),
          tripLimit: service ? service.tripLimit : 0,
          tripLimitDuration: service ? service.tripLimitDuration : ServicePromoFareIntervals.MONTH,
          autoDispatch: !!(service && service.autoDispatch),
          signatureCollectionRequired: !!(service && service.signatureCollectionRequired),
          serviceOptions: service && service.serviceOptions ? service.serviceOptions : [],
          notificationOptions: service && service.notificationOptions ? service.notificationOptions : [],
          isGroupRide: service && service.isGroupRide ? service.isGroupRide : false,
          viewOption,
          viewOptionInitVal,
          notificationMessage: (service && service.notificationMessage) || '',
          requestTimeoutDuration: service && service.requestTimeoutDuration ? service.requestTimeoutDuration : 0,
          allowMobileRequest: service ? service.allowMobileRequest : true,
          maxTravelDistance: service ? service.maxTravelDistance : 5000,
          rideReconfirmation: service ? service.rideReconfirmation : 900,
          designatedHotspotsEnabled: service ? service.designatedHotspotsEnabled : 0,
          hideService: service && service.hideService ? service.hideService : false,
          allowMultipleStop: service && service.allowMultipleStop ? service.allowMultipleStop : false
        }}
        validationSchema={this.validationSchema}
        onSubmit={this.handleFormikSubmit}
        render={InnerForm(service ? service.id : null, servicePrograms)}
      />
    )
  }

  private handleFormikSubmit = (values: InnerFormValues) => {
    const { onComponentSubmit, onStepSuccessfullyCompleted, orgId, serviceId, userId } = this.props
    if (!orgId) {
      return
    }
    const serviceProgram = values.serviceProgram ? optionToServiceProgram(values.serviceProgram) : undefined
    let service: IServiceCreateObject | IServiceUpdateObject = {
      userId,
      organizationId: orgId,
      serviceProgramId: serviceProgram ? serviceProgram.id : undefined,
      name: values.name,
      internalName: values.internalName,
      phoneNumber: values.phoneNumber,
      description: values.description,
      isMembershipRequired: values.isMembershipRequired,
      tripLimit: values.limitEnabled && values.tripLimit > 0 ? values.tripLimit : 0,
      tripLimitDuration: values.tripLimitDuration,
      viewOption: values.viewOption,
      autoDispatch: values.autoDispatch,
      signatureCollectionRequired: values.signatureCollectionRequired,
      serviceOptions: values.serviceOptions,
      onDemandTripReminder: values.notificationOptions.includes(1),
      scheduledTripReminder: values.notificationOptions.includes(2),
      willCallTripReminder: values.notificationOptions.includes(3),
      isGroupRide: values.isGroupRide,
      notificationMessage: values.notificationMessage,
      requestTimeoutDuration: values.requestTimeoutDuration,
      maxTravelDistance: values.maxTravelDistance,
      allowMobileRequest: values.allowMobileRequest,
      rideReconfirmation: values.rideReconfirmation,
      designatedHotspotsEnabled: Number(values.designatedHotspotsEnabled),
      hideService: values.hideService,
      allowMultipleStop: values.allowMultipleStop
    }
    if (values.tripLimit < 0) {
      alert('Trip limit cannot set below zero')
    }

    if (serviceId) {
      service = {
        ...service,
        id: serviceId
      } as IServiceUpdateObject
    }

    onComponentSubmit(service).then(
      (service: IServiceObject) => {
        return onStepSuccessfullyCompleted && onStepSuccessfullyCompleted()
      },
      error => console.log('error:', error)
    )
  }
}

interface InnerFormValues {
  name: string
  description: string
  internalName: string
  phoneNumber: string
  serviceProgram: ISelectOption
  isMembershipRequired: boolean
  limitEnabled: boolean
  isGroupRide: boolean
  tripLimit: number
  tripLimitDuration: string
  autoDispatch: boolean
  viewOption: string
  viewOptionInitVal: ISelectOption
  signatureCollectionRequired: boolean
  serviceOptions: IServiceOptions[]
  notificationOptions: NotificationOptions[]
  notificationMessage: string
  requestTimeoutDuration: number
  allowMobileRequest: boolean
  maxTravelDistance: number
  rideReconfirmation: number
  designatedHotspotsEnabled: number
  hideService: boolean
  allowMultipleStop: boolean
}

const InnerForm = (serviceId: string | null, servicePrograms: IServiceProgramObject[]) => (
  props: FormikProps<InnerFormValues>
) => {
  const { values, touched, errors, handleSubmit, handleChange, handleBlur, setFieldValue, setFieldTouched } = props
  const setMembershipRequired = () => {
    setFieldValue('isMembershipRequired', !values.isMembershipRequired)
  }

  const setDesignatedHotspotsEnabled = () => {
    setFieldValue('designatedHotspotsEnabled', !values.designatedHotspotsEnabled)
  }

  const toggleTripLimit = (ev: any) => {
    setFieldValue('limitEnabled', ev.target.checked)
  }

  const toggleGroupRide = (ev: any) => {
    setFieldValue('isGroupRide', ev.target.checked)
  }

  const toggleAutoDispatch = (ev: any) => {
    setFieldValue('autoDispatch', ev.target.checked)
  }

  const toggleAllowMultipleStop = (ev: any) => {
    setFieldValue('allowMultipleStop', ev.target.checked)
  }

  const toggleSignatureCollectionRequired = () => {
    setFieldValue('signatureCollectionRequired', !values.signatureCollectionRequired)
  }

  const toggleHideService = () => {
    setFieldValue('hideService', !values.hideService)
  }
  const setServiceOptions = (ev: any) => {
    const checkedValue = parseInt(ev.target.value, 10)

    if (values.serviceOptions.includes(checkedValue)) {
      values.serviceOptions.splice(values.serviceOptions.indexOf(checkedValue), 1)
    } else {
      values.serviceOptions.push(checkedValue)
    }

    setFieldValue('serviceOptions', values.serviceOptions)
  }

  const setNotificationOptions = (ev: any) => {
    const checkedValue = parseInt(ev.target.value, 10)

    if (values.notificationOptions.includes(checkedValue)) {
      values.notificationOptions.splice(values.notificationOptions.indexOf(checkedValue), 1)
    } else {
      values.notificationOptions.push(checkedValue)
    }

    setFieldValue('notificationOptions', values.notificationOptions)
  }

  const toggleAllowMobileRequest = () => {
    setFieldValue('allowMobileRequest', !values.allowMobileRequest)
  }

  const {
    limitEnabled,
    autoDispatch,
    signatureCollectionRequired,
    serviceOptions,
    notificationOptions,
    isGroupRide,
    hideService,
    allowMultipleStop
  } = values

  return (
    <form onSubmit={handleSubmit}>
      <div className='ServiceEditMainInfo flex flex-column col-8 p2 mb4'>
        <div className='mt2 h4 font-blue-grey'>{UIMessages.service.serviceInfo}</div>
        <div className='mt2'>
          <div className='flex flex-row justify-between'>
            <label className='h5 font-dark-grey'>Icon</label>
            <div className='h5 font-light-grey'>optional</div>
          </div>
          {/* Service Avatar */}
          <ImageUpload name={values.name} entityId={serviceId ? serviceId : undefined} />
          {/* Service Avatar */}
        </div>

        <div className='flex flex-row mt3'>
          <div className='col-6 pr1'>
            <label className='h5 font-dark-grey'>{UIMessages.service.serviceName}</label>
            <Field
              name='name'
              type='text'
              placeholder='Service Name'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.name && errors.name),
                'border-red focus-red': touched.name && errors.name
              })}
            />
            <div className='error'>
              {touched.name && errors.name && <span className='h6 font-red'>{errors.name}</span>}
            </div>
          </div>

          <div className='col-6'>
            <label className='h5 font-dark-grey'>{UIMessages.service.serviceProgram}</label>
            <FieldCategorySelect
              value={values.serviceProgram}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              options={serviceProgramsToOptions(servicePrograms)}
            />
            <div className='error'>
              {touched.serviceProgram && errors.serviceProgram && (
                <span className='h6 font-red'>{errors.serviceProgram}</span>
              )}
            </div>
          </div>
        </div>

        <div className='flex flex-row mt1'>
          <div className='col-6 pr1'>
            <label className='h5 font-dark-grey'>{UIMessages.service.phoneNumber}</label>
            <Field
              name='phoneNumber'
              type='text'
              placeholder='Service phone number'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.phoneNumber && errors.phoneNumber),
                'border-red focus-red': touched.phoneNumber && errors.phoneNumber
              })}
            />
            <div className='error'>
              {touched.phoneNumber && errors.phoneNumber && <span className='h6 font-red'>{errors.phoneNumber}</span>}
            </div>
          </div>
          <div className='col-6'>
            <div className='flex justify-between mt02'>
              <label className='h5 font-dark-grey'>{UIMessages.service.internalName}</label>
              <div className='h5 font-light-grey'>optional</div>
            </div>

            <Field
              name='internalName'
              type='text'
              placeholder='Internal Name'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.internalName && errors.internalName),
                'border-red focus-red': touched.internalName && errors.internalName
              })}
            />
            <div className='error'>
              {touched.internalName && errors.internalName && (
                <span className='h6 font-red'>{errors.internalName}</span>
              )}
            </div>
          </div>
        </div>

        <div className='mt1'>
          <div className='flex flex-row justify-between'>
            <label className='h5 font-dark-grey'>{UIMessages.service.serviceDes}</label>
            <div className='h5 font-light-grey'>optional</div>
          </div>
          <textarea
            name='description'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.description}
            className='rounded p1 border-light-grey4 mt02'
            placeholder='Service Description'
            rows={6}
          />
        </div>

        <div className='flex mt1 items-center'>
          <label className='h5 font-dark-grey mr2'>{UIMessages.service.membershipReq}</label>
          <Toggle state={values.isMembershipRequired} onClick={setMembershipRequired} />
        </div>
        <div className='pr1 pb1 pt1'>
          <label className='font-small bold font-grey3'>{UIMessages.service.viewOption}</label>
          <InnerRadioButton default={values.viewOptionInitVal} options={viewOptions} name={'viewOption'} />
        </div>
        <div className='font-dark-grey mt3'>
          <label className='mb0'>
            <input type='checkbox' checked={limitEnabled} onChange={toggleTripLimit} className='mr1' />
            Limit number of trips for this service.
          </label>
        </div>

        <div className='font-dark-grey mt3'>
          <label className='mb0'>
            <input type='checkbox' checked={isGroupRide} onChange={toggleGroupRide} className='mr1' />
            {UIMessages.service.groupRide}
          </label>
        </div>
        {limitEnabled && (
          <div className='flex flex-row mt2'>
            <div className='col-4 flex flex-column pr2'>
              <label className='h5'>Number completed</label>
              <Field
                type='number'
                min='0'
                name='tripLimit'
                placeholder='0'
                className='rounded p1 border-light-grey4 h4 mt02'
              />
              <div className='font-red h6'>
                {touched.tripLimit && errors.tripLimit && <div>{errors.tripLimit}</div>}
              </div>
            </div>
            <div className='col-4 flex flex-column pr2'>
              <label className='h5'>Per</label>
              <Field
                name='tripLimitDuration'
                placeholder='0'
                className='rounded p1 border-light-grey4 h4 mt02'
                render={selectInput}
              />
              <div className='font-red h6'>
                {touched.tripLimitDuration && errors.tripLimitDuration && <div>{errors.tripLimitDuration}</div>}
              </div>
            </div>
          </div>
        )}
        <div className='font-dark-grey mt3'>
          <label className='mb0'>
            <input type='checkbox' checked={autoDispatch} onChange={toggleAutoDispatch} className='mr1' />
            {UIMessages.service.autoDispatch}
          </label>
        </div>

        <div className='font-dark-grey mt3'>
          <label className='mb0'>
            <input type='checkbox' checked={allowMultipleStop} onChange={toggleAllowMultipleStop} className='mr1' />
            {UIMessages.service.multipleStop}
          </label>
        </div>

        <div className='font-dark-grey mt3'>
          <label className='mb0'>
            <input
              type='checkbox'
              checked={signatureCollectionRequired}
              onChange={toggleSignatureCollectionRequired}
              className='mr1'
            />
            {UIMessages.service.signatureCollection}
          </label>
        </div>
        <div className='flex mt1 items-center'>
          <label className='h5 font-dark-grey mr2'>{UIMessages.service.hideService}</label>
          <Toggle state={values.hideService} onClick={toggleHideService} />
        </div>
        <div className='font-dark-grey mt3'>
          <label className='font-medium bold font-grey3'>{UIMessages.service.serviceOptions}</label>
          <div className='mt1'>
            {Object.entries(ServiceOptionDisplay).map(([key, label]) => (
              <div>
                <label className='mb0'>
                  <input
                    type='checkbox'
                    key={key}
                    name={key}
                    value={IServiceOptions[key]}
                    className='mr1'
                    checked={serviceOptions.includes(IServiceOptions[key]) ? true : false}
                    onChange={setServiceOptions}
                  />
                  {label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className='flex mt1 items-center'>
          <label className='h5 font-dark-grey mr2'>{UIMessages.service.enableDesignatedHotspots}</label>
          <Toggle state={!!values.designatedHotspotsEnabled} onClick={setDesignatedHotspotsEnabled} />
        </div>

        <div className='flex flex-row mt3'>
          <div className='col-6'>
            <div className='flex justify-between'>
              <label className='h5 font-dark-grey'>{UIMessages.service.notificationMessage}</label>
              <div className='h5 font-light-grey'>optional</div>
            </div>

            <textarea
              name='notificationMessage'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.notificationMessage}
              className='rounded p1 border-light-grey4 mt02'
              placeholder='Notification Message'
              rows={6}
            />
          </div>
        </div>
        <div className='font-dark-grey mt3'>
          <label className='font-medium bold font-grey3'>{UIMessages.service.notificationOptions}</label>
          <div className='mt1'>
            {Object.entries(NotificationOptionsDisplay).map(([key, label]) => (
              <div>
                <label className='mb0'>
                  <input
                    type='checkbox'
                    key={key}
                    name={key}
                    value={NotificationOptions[key]}
                    className='mr1'
                    checked={notificationOptions.includes(NotificationOptions[key]) ? true : false}
                    onChange={setNotificationOptions}
                  />
                  {label}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className='flex flex-row mt3'>
          <div className='col-6'>
            <div className='flex justify-between'>
              <label className='h5 font-dark-grey'>{UIMessages.service.requestTimeoutDuration}</label>
              <div className='h5 font-light-grey'>optional</div>
            </div>

            <Field
              name='requestTimeoutDuration'
              type='number'
              placeholder='Request Timeout Duration'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.requestTimeoutDuration && errors.requestTimeoutDuration),
                'border-red focus-red': touched.requestTimeoutDuration && errors.requestTimeoutDuration
              })}
            />
            <div className='error'>
              {touched.requestTimeoutDuration && errors.requestTimeoutDuration && (
                <span className='h6 font-red'>{errors.requestTimeoutDuration}</span>
              )}
            </div>
          </div>
        </div>
        <div className='flex flex-row mt3'>
          <div className='col-6'>
            <div className='flex justify-between'>
              <label className='h5 font-dark-grey'>{UIMessages.service.maxTravelDistance}</label>
              <div className='h5 font-light-grey'>&nbsp;</div>
            </div>

            <Field
              name='maxTravelDistance'
              type='number'
              placeholder='Max Travel Distance'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.maxTravelDistance && errors.maxTravelDistance),
                'border-red focus-red': touched.maxTravelDistance && errors.maxTravelDistance
              })}
            />
            <div className='error'>
              {touched.maxTravelDistance && errors.maxTravelDistance && (
                <span className='h6 font-red'>{errors.maxTravelDistance}</span>
              )}
            </div>
          </div>
        </div>
        <div className='flex mt1 items-center'>
          <label className='h5 font-dark-grey mr2'>{UIMessages.service.allowMobileRequest}</label>
          <Toggle state={values.allowMobileRequest} onClick={toggleAllowMobileRequest} />
        </div>
        <div className='flex flex-row mt3'>
          <div className='col-6'>
            <div className='flex justify-between'>
              <label className='h5 font-dark-grey'>{UIMessages.service.rideReconfirmation}</label>
              <div className='h5 font-light-grey'>&nbsp;</div>
            </div>

            <Field
              name='rideReconfirmation'
              type='number'
              placeholder='Ride Reconfirmation (seconds)'
              className={classNames('rounded p1 h4 mt02', {
                'border-light-grey4': !(touched.rideReconfirmation && errors.rideReconfirmation),
                'border-red focus-red': touched.rideReconfirmation && errors.rideReconfirmation
              })}
            />
            <div className='error'>
              {touched.rideReconfirmation && errors.rideReconfirmation && (
                <span className='h6 font-red'>{errors.rideReconfirmation}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}

interface ISelectOption {
  value: string
  label: string
}

interface ICustomSelectProps {
  value: ISelectOption
  onChange: (name: string, value: ISelectOption) => void
  onBlur: (name: string, value: boolean) => void
  options: ISelectOption[]
}

const FieldCategorySelect = (props: ICustomSelectProps) => {
  const handleChange = (value: ISelectOption) => {
    props.onChange('serviceProgram', value)
  }

  const handleBlur = () => {
    props.onBlur('serviceProgram', true)
  }

  return (
    <div className='mt02'>
      <Select
        id='serviceProgram'
        options={props.options}
        onChange={handleChange}
        onBlur={handleBlur}
        value={props.value}
      />
    </div>
  )
}

const serviceProgramsToOptions = (servicePrograms: IServiceProgramObject[]): ISelectOption[] => {
  return servicePrograms.map((it: IServiceProgramObject) => ({
    value: it.id,
    label: it.name
  }))
}

const serviceProgramToOption = (serviceProgram: IServiceProgramObject): ISelectOption => {
  return {
    value: serviceProgram.id,
    label: serviceProgram.name
  }
}

const optionToServiceProgram = (option: ISelectOption): IServiceProgramObject => {
  return {
    id: option.value,
    name: option.label
  }
}

export default ServiceEditMainInfo



// WEBPACK FOOTER //
// ./src/components/services/edit/ServiceEditMainInfo.tsx